import { useMemo, useState } from 'react';

import type { TrialStatus } from '../../../context/TrialContext';
import { billingAPI } from '../../../services/api';
import type { CancelSurveyReason } from '../../../services/api';
import { trackAuthEvent } from '../../../services/analytics';
import { showDialog } from '../../../utils/dialogs';
import {
  backendDetailText,
  cancelOptionIds,
  computeOfferQueue,
  retentionOfferErrorCode,
  retentionOfferI18nKey,
} from '../planBilling';
import type {
  CancelOption,
  CancelStep,
  RetentionOfferType,
  Translate,
  UserProfile,
} from '../profileTypes';

interface UseCancelSubscriptionOptions {
  profile: UserProfile | null;
  trialStatus: TrialStatus | null;
  isCardBackedTrial: boolean;
  loadProfile: () => Promise<void>;
  t: Translate;
}

/** Cancel-subscription modal: survey, retention offers, then confirm. */
export function useCancelSubscription({
  profile,
  trialStatus,
  isCardBackedTrial,
  loadProfile,
  t,
}: UseCancelSubscriptionOptions) {
  const [showCancelSubscription, setShowCancelSubscription] = useState(false);
  // BIL-L18 (4): cancelling a paused subscription ends it now (the backend
  // ends a paused subscription immediately), so the copy says so.
  const isPaused = (profile?.subscription?.status || '').toLowerCase() === 'paused';
  const cancelOptionsData = useMemo(
    () => {
      const cardTrial = trialStatus?.active && trialStatus.kind === 'card_backed';
      const options = {
        period_end: {
          id: 'period_end' as const,
          title: cardTrial
            ? t('profile.cancelTrialAtEndButton')
            : isPaused
              ? t('profile.cancelPausedButton')
              : t('profile.cancelAtPeriodEndButton'),
          description: cardTrial
            ? t('profile.cancelTrialAtEndDescription')
            : isPaused
              ? t('profile.cancelPausedDescription')
              : t('profile.cancelOptionScheduledDescription'),
        },
        // Only offered to a card-backed trial (BIL-L12/L15).
        immediate: {
          id: 'immediate' as const,
          title: t('profile.cancelTrialNowButton'),
          description: t('profile.cancelTrialNowDescription'),
        },
      };
      return cancelOptionIds(isCardBackedTrial).map((id) => options[id]);
    },
    [t, trialStatus?.active, trialStatus?.kind, isCardBackedTrial, isPaused]
  );
  const cancelReasonOptions = useMemo(
    () => (['too_expensive', 'not_using', 'missing_features', 'switching', 'temporary_break', 'other'] as const)
      .map((id) => ({ id, label: t(`profile.cancelReason.${id}`) })),
    [t],
  );
  const [cancelOption, setCancelOption] = useState<CancelOption>('period_end');
  const [cancelStep, setCancelStep] = useState<CancelStep>('survey');
  const [cancelReason, setCancelReason] = useState<CancelSurveyReason | null>(null);
  const [cancelReasonNote, setCancelReasonNote] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [offerQueue, setOfferQueue] = useState<RetentionOfferType[]>([]);
  const [offerIndex, setOfferIndex] = useState(0);

  const handleOpenCancelSubscription = () => {
    trackAuthEvent('subscription_cancel_modal_opened', {
      plan_tier: profile?.subscription?.plan_tier,
    });
    setCancelOption('period_end');
    setCancelReason(null);
    setCancelReasonNote('');
    setOfferQueue([]);
    setOfferIndex(0);
    setCancelStep('survey');
    setShowCancelSubscription(true);
    trackAuthEvent('cancel_survey_shown', {
      plan_tier: profile?.subscription?.plan_tier,
    });
  };

  const offerI18nKey = (offerType: RetentionOfferType) =>
    retentionOfferI18nKey(offerType, profile?.subscription?.plan_tier);

  const showOfferAt = (queue: RetentionOfferType[], index: number) => {
    const offerType = queue[index];
    if (!offerType) {
      setCancelStep('confirm');
      return;
    }
    setOfferIndex(index);
    setCancelStep('offer');
    trackAuthEvent('retention_offer_shown', {
      offer_type: offerType,
      trigger: 'cancel_flow',
      reason: cancelReason,
      is_trial: isCardBackedTrial,
    });
  };

  const handleCancelSurveyContinue = () => {
    if (!cancelReason || cancelLoading) return;
    const reasonNote = cancelReasonNote.trim();
    // Fire-and-forget: kicked off before we move to the next step, but a
    // failure here must never block the cancel flow itself.
    billingAPI.submitCancelSurvey({
      reason: cancelReason,
      reason_note: reasonNote || undefined,
    }).catch((error: any) => {
      console.warn('[cancel-survey] submit failed', error?.response?.status);
    });
    trackAuthEvent('cancel_survey_submitted', {
      reason: cancelReason,
      has_note: reasonNote.length > 0,
    });
    const queue = computeOfferQueue(
      profile?.subscription?.cancel_at_period_end,
      cancelReason,
      isCardBackedTrial,
      isPaused,
    );
    setOfferQueue(queue);
    if (queue.length === 0) {
      setCancelStep('confirm');
    } else {
      showOfferAt(queue, 0);
    }
  };

  const handleDeclineOffer = () => {
    const offerType = offerQueue[offerIndex];
    if (offerType) {
      trackAuthEvent('retention_offer_declined', {
        offer_type: offerType,
        trigger: 'cancel_flow',
        reason: cancelReason,
        is_trial: isCardBackedTrial,
      });
    }
    showOfferAt(offerQueue, offerIndex + 1);
  };

  const handleCancelSubscription = async () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    try {
      const cancelingCardTrial = isCardBackedTrial;
      const cancelingPaused = isPaused;
      // Paid subscribers cancel at period end only (BIL-L12).
      const terminateNow = cancelingCardTrial && cancelOption === 'immediate';
      trackAuthEvent('subscription_cancel_submitted', {
        cancel_type: terminateNow ? 'immediate' : 'period_end',
      });
      // Primary write is the fire-and-forget cancel-survey call in
      // handleCancelSurveyContinue. Reason/note are sent again here as a
      // redundant fallback in case that earlier POST failed (flaky
      // network/5xx) — backend prefers the survey row when one exists.
      await billingAPI.cancelSubscription(terminateNow, cancelReason || undefined, cancelReasonNote);
      setShowCancelSubscription(false);
      await loadProfile();
      trackAuthEvent('subscription_cancel_completed', {
        cancel_type: terminateNow ? 'immediate' : 'period_end',
      });
      showDialog(
        t('common.success'),
        cancelingCardTrial
          ? terminateNow
            ? t('profile.cancelTrialSuccessImmediate')
            : t('profile.cancelTrialSuccessScheduled')
          : cancelingPaused
            ? t('profile.cancelPausedSuccess')
            : t('profile.cancelSuccessScheduled')
      );
    } catch (error: any) {
      const errorStatus = error?.response?.status;
      const errorType = errorStatus ? `http_${errorStatus}` : (error?.message ? 'exception' : 'unknown');
      trackAuthEvent('subscription_cancel_failed', {
        error_type: errorType,
      });
      const detail = backendDetailText(error);
      let message = t('profile.cancelFailed');
      if (errorStatus === 400 && /no active subscription/i.test(detail)) {
        message = t('profile.noActiveSubscription');
      } else if (errorStatus === 502) {
        message = t('profile.billingActionTemporarilyUnavailable');
      } else if (detail && !errorStatus) {
        message = detail;
      }
      showDialog(t('common.error'), message);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleAcceptOffer = async () => {
    if (cancelLoading) return;
    const offerType = offerQueue[offerIndex];
    if (!offerType) return;
    setCancelLoading(true);
    const offerEventProps = {
      offer_type: offerType,
      trigger: 'cancel_flow',
      reason: cancelReason,
      is_trial: isCardBackedTrial,
    };
    try {
      if (offerType === 'first_month_discount') {
        await billingAPI.firstMonthDiscount();
      } else if (offerType === 'pause') {
        await billingAPI.pauseSubscription();
      } else {
        await billingAPI.applyRetentionOffer();
      }
      setShowCancelSubscription(false);
      await loadProfile();
      trackAuthEvent('retention_offer_accepted', offerEventProps);
      showDialog(
        t('common.success'),
        t(`profile.retentionOffers.${offerI18nKey(offerType)}.success`),
      );
    } catch (error: any) {
      const errorStatus = error?.response?.status;
      const errorCode = retentionOfferErrorCode(error);
      trackAuthEvent('retention_offer_error', {
        offer_type: offerType,
        error_code: errorCode,
      });
      if (errorStatus === 409 || errorStatus === 400) {
        // Offer already used / not eligible — skip forward gracefully
        // instead of leaving the user stuck on a dead offer.
        showOfferAt(offerQueue, offerIndex + 1);
      } else {
        const message = backendDetailText(error) || t('profile.retentionOfferFailed');
        showDialog(t('common.error'), message);
      }
    } finally {
      setCancelLoading(false);
    }
  };

  return {
    showCancelSubscription,
    closeCancelSubscription: () => setShowCancelSubscription(false),
    isPaused,
    handleOpenCancelSubscription,
    cancelOptionsData,
    cancelReasonOptions,
    cancelOption,
    setCancelOption,
    cancelStep,
    cancelReason,
    setCancelReason,
    cancelReasonNote,
    setCancelReasonNote,
    cancelLoading,
    offerQueue,
    offerIndex,
    offerI18nKey,
    handleCancelSurveyContinue,
    handleDeclineOffer,
    handleAcceptOffer,
    handleCancelSubscription,
  };
}
